import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Popover
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { type Dayjs } from 'dayjs';
import {
  bindPopover,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import { useState } from 'react';

import { DATE_FORMAT } from '@/constants/date.constants.ts';

import { TIME_PRESETS } from './TimeFilter.constants';
import useStyles from './TimeFilter.styles.ts';
import type {
  TimeFilterProps,
  TimePresetField,
  TimePresetKey
} from './TimeFilter.types';
import { getTimeFilterValues } from './TimeFilter.utils.ts';

const TimeFilter = ({
  createdTimeLabel = 'Created Time',
  disableTimeFilterType,
  lastUpdatedTimeLabel = 'Last Updated Time',
  onChange,
  selectedTime
}: TimeFilterProps) => {
  const { classes } = useStyles();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'timeFramePopover'
  });
  // edits to the custom date pickers, valid only for the selectedTime they were made against
  const [draft, setDraft] = useState<{
    source: string | undefined;
    from: Dayjs | null;
    to: Dayjs | null;
  } | null>(null);
  if (draft !== null && draft.source !== selectedTime) {
    // parent selection changed: drop the stale draft so it cannot resurface later
    setDraft(null);
  }

  const { timeFilterKey, timeFilterField, prefix } =
    getTimeFilterValues(selectedTime);

  // the custom range from the parent selection, shown unless the user edited it
  let selectedFrom: Dayjs | null = null;
  let selectedTo: Dayjs | null = null;
  if (prefix === 'custom') {
    const [from, to] = timeFilterKey.replace('custom-', '').split(',');
    selectedFrom = from ? dayjs(from) : null;
    selectedTo = to ? dayjs(to) : null;
  }
  const isDraftCurrent = draft !== null && draft.source === selectedTime;
  const fromDate = draft !== null && isDraftCurrent ? draft.from : selectedFrom;
  const toDate = draft !== null && isDraftCurrent ? draft.to : selectedTo;
  const setFromDate = (from: Dayjs | null) =>
    setDraft({ source: selectedTime, from, to: toDate });
  const setToDate = (to: Dayjs | null) =>
    setDraft({ source: selectedTime, from: fromDate, to });

  const customTimeInputsInvalid = Boolean(
    fromDate && toDate && fromDate >= toDate
  );

  const handleSelect = (key: TimePresetKey) => {
    popupState.close();
    onChange?.(`${key}|${timeFilterField}`);
  };

  const handleTimeFieldSelect = (field: TimePresetField) => {
    popupState.close();
    onChange?.(`${timeFilterKey}|${field}`);
  };

  const handleApply = () => {
    const from = fromDate ? dayjs(fromDate).format(DATE_FORMAT) : '';
    const to = toDate ? dayjs(toDate).format(DATE_FORMAT) : '';
    if (from || to) {
      onChange?.(`custom-${from},${to}|${timeFilterField}`);
    }
    popupState.close();
  };

  const Toggle = (
    <Button
      {...bindTrigger(popupState)}
      type="button"
      className={classes.timefilterBtn}
      variant="outlined"
      color="secondary"
      endIcon={<FontAwesomeIcon icon={faAngleDown} />}
    >
      {selectedTime?.includes('custom')
        ? 'Custom'
        : TIME_PRESETS.find((preset) => preset.key === timeFilterKey)?.label}
    </Button>
  );

  return (
    <>
      {Toggle}
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <List
          disablePadding
          component="div"
          className={classes.filterList}
          subheader={<ListSubheader component="div">Presets</ListSubheader>}
        >
          <ListItem disablePadding component="div">
            <List className={classes.presetList} disablePadding component="div">
              {TIME_PRESETS.map((preset) => (
                <ListItemButton
                  key={preset.key}
                  disableRipple
                  className={classes.presetListItem}
                  onClick={() => handleSelect(preset.key)}
                >
                  <ListItemText primary={preset.label} />
                </ListItemButton>
              ))}
            </List>
          </ListItem>
          <ListSubheader component="div">Custom Time</ListSubheader>
          <ListItem
            disablePadding
            className={classes.customTime}
            component="div"
          >
            <DatePicker
              value={fromDate}
              disableFuture
              slotProps={{
                textField: {
                  size: 'small',
                  error: customTimeInputsInvalid,
                  slotProps: {
                    inputLabel: { shrink: true }
                  }
                },
                openPickerButton: {
                  size: 'small'
                }
              }}
              label="From"
              onChange={(newValue) => setFromDate(newValue)}
            />
            <DatePicker
              value={toDate}
              disableFuture
              slotProps={{
                textField: {
                  size: 'small',
                  error: customTimeInputsInvalid,
                  slotProps: {
                    inputLabel: { shrink: true }
                  }
                },
                openPickerButton: {
                  size: 'small'
                }
              }}
              label="To"
              onChange={(newValue) => setToDate(newValue)}
            />
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={customTimeInputsInvalid}
            >
              Apply
            </Button>
          </ListItem>
          {/* If disableTibleFilterType is true, then the buttons to choose which type of time filter to apply is hidden */}
          {!disableTimeFilterType && (
            <>
              <ListSubheader component="div">Filter By</ListSubheader>
              <ListItem sx={{ py: 2 }}>
                <ButtonGroup size="small" variant="outlined">
                  <Button
                    onClick={() => handleTimeFieldSelect('C')}
                    variant={timeFilterField === 'C' ? 'contained' : 'outlined'}
                    color="primary"
                  >
                    {createdTimeLabel}
                  </Button>
                  <Button
                    onClick={() => handleTimeFieldSelect('U')}
                    variant={timeFilterField === 'U' ? 'contained' : 'outlined'}
                    color="primary"
                  >
                    {lastUpdatedTimeLabel}
                  </Button>
                </ButtonGroup>
              </ListItem>
            </>
          )}
        </List>
      </Popover>
    </>
  );
};

export default TimeFilter;
